import React, { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';
import { app, db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaTwitter, FaInstagram, FaBehance, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        {/* Hero Section */}
        <section className="px-4 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Get in touch</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Let's Start a Conversation
              </h1>
              <p className="text-lg sm:text-xl text-perspective-cloud/85 max-w-3xl mx-auto leading-relaxed">
                Let's discuss how we can help bring your ideas to life. We're here to answer your questions and explore how we can work together.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-white/5 backdrop-blur border border-white/10 p-8 rounded-3xl shadow-elevated">
                  <h2 className="text-3xl font-bold text-white mb-6">Send us a message</h2>
                  <ContactForm />
                </div>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">Get in touch</h2>
                  <p className="text-lg text-perspective-cloud/80 mb-8">
                    Ready to start your next project? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                  </p>
                </div>
                
                <div className="space-y-6">
                  <ContactInfo
                    icon={FaMapMarkerAlt}
                    title="Our Location"
                    info="Durban, South Africa"
                  />
                  <ContactInfo
                    icon={FaPhone}
                    title="Call Us"
                    info="+27 69 339 5619"
                  />
                  <ContactInfo
                    icon={FaEnvelope}
                    title="Email Us"
                    info="info@perspectivehq.co.za"
                  />
                </div>

                {/* Social Links */}
                <div className="pt-8">
                  <h3 className="text-xl font-semibold text-white mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300">
                      <FaTwitter size={20} />
                    </a>
                    <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300">
                      <FaInstagram size={20} />
                    </a>
                    <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center hover:bg-perspective-sage hover:border-perspective-sage hover:text-perspective-carbon transition-all duration-300">
                      <FaBehance size={20} />
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl mb-6">
                  Creating digital experiences that drive growth and innovation. We're passionate about building solutions that make a difference.
                </p>
                <div className="flex gap-3">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await emailjs.sendForm('service_yj8x2ck', 'template_l2ogecb', e.target, '6hQ12bB0onqAOf-vk');
      await addDoc(collection(db, "inquiries"), {
        ...formData,
        timestamp: new Date(),
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
      });

      setIsModalOpen(true);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-perspective-cloud/80 mb-3">First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all duration-300"
              placeholder="Enter your first name"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-perspective-cloud/80 mb-3">Last Name</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all duration-300"
              placeholder="Enter your last name"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-perspective-cloud/80 mb-3">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all duration-300"
            placeholder="Enter your email address"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-perspective-cloud/80 mb-3">Message</label>
          <textarea
            name="message"
            rows={6}
            value={formData.message}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all duration-300 resize-none"
            placeholder="Tell us about your project or question..."
          ></textarea>
        </div>
        <button
          type="submit"
          className="w-full group bg-perspective-sage text-perspective-carbon py-4 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
        >
          Send Message
          <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
        </button>
      </form>

      {/* Success Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div 
            className="bg-perspective-carbon border border-white/10 rounded-3xl p-8 w-11/12 md:w-1/3 shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-perspective-sage/15 rounded-full flex items-center justify-center mx-auto mb-6 border border-perspective-sage/20">
                <FaEnvelope className="text-perspective-sage text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Message Sent Successfully!</h2>
              <p className="text-perspective-cloud/80 mb-8">Thank you for reaching out. We'll get back to you within 24 hours.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => (window.location.href = '/')}
                  className="bg-perspective-sage text-perspective-carbon px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
                >
                  Go to Homepage
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-white/5 border border-white/10 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

const ContactInfo = ({ icon: Icon, title, info }) => {
  return (
    <div className="flex gap-4 p-6 bg-white/5 backdrop-blur border border-white/10 rounded-2xl hover:border-perspective-sage/30 transition-all duration-300">
      <div className="w-12 h-12 bg-perspective-sage/15 rounded-full flex items-center justify-center flex-shrink-0 border border-perspective-sage/20">
        <Icon className="text-perspective-sage text-xl" />
      </div>
      <div>
        <h3 className="font-bold text-lg text-white mb-2">{title}</h3>
        <p className="text-perspective-cloud/80 text-lg">{info}</p>
      </div>
    </div>
  );
};

export default Contact;
